import protocoloService from "@/services/processos-eletronicos/protocoloService";
import { PortalError } from "@/types/portal-error";

const CODIGOS_TIPO_DOCUMENTO_SEM_PECA_UNICA = new Set<number>([
  9,
  22,
  29,
  38,
  50,
  52,
  1019,
  4020,
  4035,
  4050,
  4051,
  4056,
  4059,
]);

export interface ResultadoProcessamentoPecaUnica {
  acao: "modal" | "download";
  arquivo: Blob;
  pdfData?: Uint8Array;
  numeroPaginas: number;
}

interface PodeVisualizarPecaUnicaParams {
  codProtocoloCriptografado?: string | null;
  tipo?: string | null;
  codigoTipoDocumento?: number | null;
}

function normalizarTipo(tipo?: string | null): "processo" | "protocolo" {
  return tipo?.toLowerCase() === "processo" ? "processo" : "protocolo";
}

function normalizarCodigoCriptografado(codigo?: string | number | null): string {
  const codigoNormalizado = String(codigo || "").trim();
  if (!codigoNormalizado) {
    throw new PortalError("Código criptografado inválido para baixar arquivo.");
  }
  return codigoNormalizado;
}

function sanitizarNomeArquivo(valor: string): string {
  return valor.replace(/[\\/:*?"<>|]/g, "-").trim();
}

function criarResultadoDownload(
  arquivo: Blob,
  numeroPaginas: number
): ResultadoProcessamentoPecaUnica {
  return {
    acao: "download",
    arquivo,
    numeroPaginas,
  };
}

export function podeVisualizarPecaUnica({
  codProtocoloCriptografado,
  tipo,
  codigoTipoDocumento,
}: PodeVisualizarPecaUnicaParams): boolean {
  const codigoNormalizado = String(codProtocoloCriptografado || "").trim();
  if (!codigoNormalizado) {
    return false;
  }

  if (normalizarTipo(tipo) !== "protocolo") {
    return true;
  }

  return !CODIGOS_TIPO_DOCUMENTO_SEM_PECA_UNICA.has(Number(codigoTipoDocumento));
}

export function montarNomeArquivoPecaUnica(
  tipo: string | null | undefined,
  identificador: string | number | null | undefined
): string {
  const tipoNormalizado = normalizarTipo(tipo);
  const valorSeguro = sanitizarNomeArquivo(String(identificador || "sem-codigo"));
  return `${tipoNormalizado}-${valorSeguro || "sem-codigo"}`;
}

export function montarNomeArquivoAto(
  identificador: string | number | null | undefined
): string {
  const valorSeguro = sanitizarNomeArquivo(String(identificador || "sem-codigo"));
  return `ato-${valorSeguro || "sem-codigo"}`;
}

export function montarNomeArquivoMultimidia(
  nomeOriginal: string | null | undefined,
  codCriptografado: string | number | null | undefined
): string {
  const base = String(nomeOriginal || `midia-${codCriptografado || "sem-codigo"}`);
  return sanitizarNomeArquivo(base) || "midia-sem-codigo";
}

export async function baixarArquivoPecaUnica(
  codProtocoloCriptografado: string,
  carimbo = "S"
): Promise<Blob> {
  return protocoloService.downloadPecaUnicaPdf(
    normalizarCodigoCriptografado(codProtocoloCriptografado),
    carimbo
  );
}

export async function baixarArquivoAto(
  codArquivoDigitalCriptografado: string
): Promise<Blob> {
  return protocoloService.downloadAtoPdf(
    normalizarCodigoCriptografado(codArquivoDigitalCriptografado)
  );
}

export async function baixarArquivoMultimidia(
  codCriptografadoMidia: string
): Promise<Blob> {
  return protocoloService.downloadMidiaPecaUnicaPdf(
    normalizarCodigoCriptografado(codCriptografadoMidia)
  );
}

export async function analisarPaginacaoPecaUnica(
  arquivo: Blob
): Promise<{ arrayBuffer?: ArrayBuffer; numeroPaginas: number }> {
  if (arquivo.size > 20 * 1024 * 1024) {
    return { numeroPaginas: 101 };
  }

  const arrayBuffer = await arquivo.arrayBuffer();
  const textoPdf = new TextDecoder("latin1").decode(new Uint8Array(arrayBuffer));

  const matchesCount = textoPdf.match(/\/Count\s+(\d+)/g) || [];
  const paginasPorCount = matchesCount
    .map((m) => Number(m.replace(/[^0-9]/g, "")))
    .filter((n) => Number.isFinite(n) && n > 0);
  const maiorCount = paginasPorCount.length ? Math.max(...paginasPorCount) : 0;

  if (maiorCount > 0) {
    return { arrayBuffer, numeroPaginas: maiorCount };
  }

  const ocorrenciasPage = textoPdf.match(/\/Type\s*\/Page(?!s)\b/g);
  return {
    arrayBuffer,
    numeroPaginas: ocorrenciasPage ? ocorrenciasPage.length : 0,
  };
}

export async function processarPecaUnica(
  codProtocoloCriptografado: string,
  carimbo = "S"
): Promise<ResultadoProcessamentoPecaUnica> {
  const arquivo = await baixarArquivoPecaUnica(codProtocoloCriptografado, carimbo);
  const { numeroPaginas, arrayBuffer } = await analisarPaginacaoPecaUnica(arquivo);

  if (numeroPaginas > 100 || !arrayBuffer) {
    return criarResultadoDownload(arquivo, numeroPaginas);
  }

  return {
    acao: "modal",
    arquivo,
    numeroPaginas,
    pdfData: new Uint8Array(arrayBuffer),
  };
}

export async function processarAto(
  codArquivoDigitalCriptografado: string
): Promise<ResultadoProcessamentoPecaUnica> {
  const arquivo = await baixarArquivoAto(codArquivoDigitalCriptografado);
  return criarResultadoDownload(arquivo, 0);
}
